/**
 * wsMath — pure option math for the Charts "Options Workspace" layout.
 *
 * No React, no I/O. Everything is in Delta-NATIVE units:
 *   - prices / premiums: USD per 1 unit of the base asset (as the chain quotes them);
 *   - a position's money = premium × lots × contractValue (BTC 0.001, ETH 0.01) → USD.
 * The UI converts USD→INR for display only.
 *
 * Valuation model: Black-Scholes with r = 0 (Delta crypto options are quoted that way)
 * on each leg's OWN implied vol and OWN expiry. "At expiry" means the EARLIEST expiry
 * among the enabled legs (the usual convention for calendars): legs expiring then are
 * worth intrinsic, later legs are still valued with BS at their remaining time.
 */

export type Side = 'BUY' | 'SELL';
export type OptType = 'CE' | 'PE';

export interface WsLeg {
  id: string;
  action: Side;
  optionType: OptType;
  strike: number;
  expiry: string;        // YYYY-MM-DD
  settleMs: number;      // settlement instant (Delta: 12:00 UTC on the expiry date)
  lots: number;          // contracts (1 lot = contractValue of the base)
  /** Entry premium per unit of base (USD). MKT legs follow the live mark. */
  price: number;
  priceMode: 'MKT' | 'LMT';
  enabled: boolean;
  /** IV (fraction) used to value the leg before expiry — the live mark IV. */
  iv: number;
  /** Delta product symbol, e.g. C-BTC-79000-011126. */
  symbol: string;
  /** Leg comes from an already-open basket (entry = its fill). Analysed, never re-placed. */
  held?: boolean;
  heldBasketId?: number;
  /** Held leg marked to be CLOSED by a pending roll: valued as booked at `closeMark`. */
  closing?: boolean;
  closeMark?: number;
  /** New leg that is part of a pending adjustment of held basket #rollBasketId (not a new basket). */
  rollBasketId?: number;
  /** Held leg id this leg replaces (Roll). */
  rollOf?: string;
  /** Live mark (layered on each render). For held legs `price` stays the fill. */
  mark?: number;
}

const YEAR_MS = 365 * 24 * 3600 * 1000;

/** Standard normal CDF (Abramowitz–Stegun 7.1.26, |err| < 7.5e-8). */
export function normCdf(x: number): number {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const z = Math.abs(x) / Math.SQRT2;
  const t = 1 / (1 + p * z);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-z * z);
  return 0.5 * (1 + sign * y);
}
export const normPdf = (x: number) => Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);

/** Black-Scholes (r = 0) premium. Falls back to intrinsic at/after expiry or with no vol. */
export function bsPrice(type: OptType, S: number, K: number, tYears: number, sigma: number): number {
  const intrinsic = type === 'CE' ? Math.max(0, S - K) : Math.max(0, K - S);
  if (!(tYears > 0) || !(sigma > 0) || !(S > 0) || !(K > 0)) return intrinsic;
  const sd = sigma * Math.sqrt(tYears);
  const d1 = (Math.log(S / K) + 0.5 * sd * sd) / sd;
  const d2 = d1 - sd;
  return type === 'CE' ? S * normCdf(d1) - K * normCdf(d2) : K * normCdf(-d2) - S * normCdf(-d1);
}

/** BS greeks per 1 unit of base: delta, gamma, theta (USD/day), vega (USD per 1 vol point). */
export function bsGreeks(type: OptType, S: number, K: number, tYears: number, sigma: number) {
  if (!(tYears > 0) || !(sigma > 0) || !(S > 0) || !(K > 0)) {
    const itm = type === 'CE' ? S > K : S < K;
    return { delta: itm ? (type === 'CE' ? 1 : -1) : 0, gamma: 0, theta: 0, vega: 0 };
  }
  const sd = sigma * Math.sqrt(tYears);
  const d1 = (Math.log(S / K) + 0.5 * sd * sd) / sd;
  const pdf = normPdf(d1);
  return {
    delta: type === 'CE' ? normCdf(d1) : normCdf(d1) - 1,
    gamma: pdf / (S * sd),
    theta: -(S * pdf * sigma) / (2 * Math.sqrt(tYears)) / 365,
    vega: S * pdf * Math.sqrt(tYears) / 100,
  };
}

const sgn = (a: Side) => (a === 'BUY' ? 1 : -1);

/** A leg of the NEW basket being built (not held, not part of a pending roll). */
export const isNewLeg = (l: WsLeg) => !l.held && l.rollBasketId == null;
/** Legs the strike controls may move. */
const fixedLeg = (l: WsLeg) => !isNewLeg(l);

/** Value of one leg's option (per unit of base) at spot S, time `atMs`, IV shifted by `ivShift` vol points. */
export function legValue(leg: WsLeg, S: number, atMs: number, ivShift = 0): number {
  // A held leg being closed by a roll is booked at its current mark — a constant.
  if (leg.closing && leg.closeMark != null) return leg.closeMark;
  const t = Math.max(0, leg.settleMs - atMs) / YEAR_MS;
  const iv = Math.max(0.01, (leg.iv || 0) + ivShift / 100);
  return bsPrice(leg.optionType, S, leg.strike, t, iv);
}

/** Position P&L (USD) at spot S and time atMs. */
export function positionPnl(legs: WsLeg[], S: number, atMs: number, cv: number, ivShift = 0): number {
  let pnl = 0;
  for (const l of legs) {
    if (!l.enabled) continue;
    pnl += sgn(l.action) * (legValue(l, S, atMs, ivShift) - l.price) * l.lots * cv;
  }
  return pnl;
}

/** The "at expiry" horizon: earliest settlement among enabled legs. */
export function horizonMs(legs: WsLeg[]): number | null {
  const on = legs.filter(l => l.enabled && !l.closing);
  if (!on.length) return null;
  return Math.min(...on.map(l => l.settleMs));
}

/** Net premium (USD): + = credit received, − = debit paid. */
export function netPremium(legs: WsLeg[], cv: number): number {
  return legs.filter(l => l.enabled && !l.closing).reduce((s, l) => s - sgn(l.action) * l.price * l.lots * cv, 0);
}

/** Spot grid for the payoff: wide enough to cover every strike and ±4σ of the horizon move. */
export function spotGrid(legs: WsLeg[], spot: number, sigma: number, tYears: number, n = 241): number[] {
  const strikes = legs.filter(l => l.enabled && !l.closing).map(l => l.strike);
  const move = Math.max(0.04, 4 * sigma * Math.sqrt(Math.max(tYears, 1 / 365)));
  let lo = Math.min(spot * (1 - move), ...strikes.map(k => k * 0.97));
  let hi = Math.max(spot * (1 + move), ...strikes.map(k => k * 1.03));
  lo = Math.max(lo, spot * 0.2);
  hi = Math.min(hi, spot * 3);
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(lo + ((hi - lo) * i) / (n - 1));
  return out;
}

export interface WsStats {
  maxProfit: number;          // USD; Infinity when unbounded
  maxLoss: number;            // USD (negative); -Infinity when unbounded
  breakevens: number[];
  pop: number;                // probability of profit at the horizon (0..1)
  ev: number;                 // expected value (USD) at the horizon under the lognormal
  net: number;                // net premium (+ credit)
  rr: number | null;          // reward : risk (maxProfit / |maxLoss|) when both finite
  horizon: number | null;
}

/** Lognormal CDF of S_T given S0, σ, T (zero drift in log-price, matching BS r=0). */
export function lnCdf(x: number, S0: number, sigma: number, tYears: number): number {
  if (x <= 0) return 0;
  const sd = sigma * Math.sqrt(Math.max(tYears, 1e-9));
  return normCdf((Math.log(x / S0) + 0.5 * sd * sd) / sd);
}

export function computeStats(legs: WsLeg[], spot: number, cv: number, atmIv: number, nowMs: number): WsStats {
  const H = horizonMs(legs);
  const net = netPremium(legs, cv);
  if (H == null || !(spot > 0)) return { maxProfit: 0, maxLoss: 0, breakevens: [], pop: 0, ev: 0, net, rr: null, horizon: null };
  const tH = Math.max(0, H - nowMs) / YEAR_MS;
  const sigma = atmIv > 0 ? atmIv : 0.5;
  const grid = spotGrid(legs, spot, sigma, tH, 801);
  const pnl = grid.map(s => positionPnl(legs, s, H, cv));

  // Spot can't go below 0, so the LEFT tail is always bounded: include the exact
  // P&L at S≈0 (puts pay most there). Only the RIGHT tail can be unbounded — when the
  // P&L is still trending at the grid's upper edge (net long / short calls).
  const at0 = positionPnl(legs, 1e-6, H, cv);
  const slopeHi = (pnl[pnl.length - 1] - pnl[pnl.length - 2]) / (grid[grid.length - 1] - grid[grid.length - 2]);
  const EPS = 1e-7 * cv;
  let maxProfit = Math.max(...pnl, at0);
  let maxLoss = Math.min(...pnl, at0);
  if (slopeHi > EPS) maxProfit = Infinity;
  if (slopeHi < -EPS) maxLoss = -Infinity;

  const breakevens: number[] = [];
  for (let i = 1; i < grid.length; i++) {
    const a = pnl[i - 1], b = pnl[i];
    if ((a < 0 && b >= 0) || (a >= 0 && b < 0)) {
      const x = grid[i - 1] + (grid[i] - grid[i - 1]) * (0 - a) / (b - a || 1);
      if (!breakevens.length || Math.abs(x - breakevens[breakevens.length - 1]) > spot * 1e-4) breakevens.push(x);
    }
  }

  // POP / EV under the lognormal at the horizon, integrating cell by cell over the grid,
  // plus the two tails beyond it (valued at the edge P&L, which is where most of the
  // unbounded risk sits — conservative and stable).
  let pop = 0, ev = 0, prevCdf = 0;
  for (let i = 0; i <= grid.length; i++) {
    const cdf = i < grid.length ? lnCdf(i === 0 ? grid[0] : (grid[i - 1] + grid[i]) / 2, spot, sigma, tH) : 1;
    const mass = cdf - prevCdf;
    // cell i spans [mid(i-1,i), mid(i,i+1)] → use pnl at grid[i-1] for the cell that just closed
    const val = i === 0 ? pnl[0] : pnl[i - 1];
    if (val > 0) pop += mass;
    ev += mass * val;
    prevCdf = cdf;
  }
  const rr = Number.isFinite(maxProfit) && Number.isFinite(maxLoss) && maxLoss < 0 ? maxProfit / Math.abs(maxLoss) : null;
  return { maxProfit, maxLoss, breakevens, pop: Math.max(0, Math.min(1, pop)), ev, net, rr, horizon: H };
}

/** Net position greeks in money terms: Δ in base units (e.g. BTC), Γ per USD, Θ USD/day, Vega USD/vol-pt. */
export function netGreeks(legs: WsLeg[], spot: number, atMs: number, cv: number, ivShift = 0) {
  const tot = { delta: 0, gamma: 0, theta: 0, vega: 0 };
  const rows = legs.filter(l => l.enabled && !l.closing).map(l => {
    const t = Math.max(0, l.settleMs - atMs) / YEAR_MS;
    const g = bsGreeks(l.optionType, spot, l.strike, t, Math.max(0.01, l.iv + ivShift / 100));
    const m = sgn(l.action) * l.lots * cv;
    const r = { leg: l, qty: m, delta: g.delta * m, gamma: g.gamma * m, theta: g.theta * m, vega: g.vega * m, iv: l.iv };
    tot.delta += r.delta; tot.gamma += r.gamma; tot.theta += r.theta; tot.vega += r.vega;
    return r;
  });
  return { rows, total: tot };
}

/** 1σ / 2σ expected-move bands at the horizon. */
export function sdBands(spot: number, sigma: number, tYears: number) {
  const sd = sigma * Math.sqrt(Math.max(tYears, 0));
  return { lo1: spot * Math.exp(-sd), hi1: spot * Math.exp(sd), lo2: spot * Math.exp(-2 * sd), hi2: spot * Math.exp(2 * sd) };
}

// ── strike helpers ───────────────────────────────────────────────────────────

/** Move a strike `steps` listed strikes up (+) or down (−) within `strikes` (ascending). */
export function stepStrike(strikes: number[], k: number, steps: number): number {
  if (!strikes.length) return k;
  let i = strikes.findIndex(s => s >= k);
  if (i < 0) i = strikes.length - 1;
  if (strikes[i] !== k && steps < 0) i = Math.max(0, i);       // between strikes: first step down lands on the lower one
  else if (strikes[i] !== k && steps > 0) i = i - 1;
  return strikes[Math.max(0, Math.min(strikes.length - 1, i + steps))];
}

export function nearestStrike(strikes: number[], x: number): number | null {
  if (!strikes.length) return null;
  return strikes.reduce((p, c) => (Math.abs(c - x) < Math.abs(p - x) ? c : p), strikes[0]);
}

/** Shift every leg the same number of listed strikes. */
export function shiftLegs(legs: WsLeg[], strikesFor: (iso: string) => number[], steps: number): WsLeg[] {
  return legs.map(l => (fixedLeg(l) ? l : { ...l, strike: stepStrike(strikesFor(l.expiry), l.strike, steps) }));
}

/** Widen (+) / narrow (−): legs above the structure's centre move up, below move down. */
export function widenLegs(legs: WsLeg[], strikesFor: (iso: string) => number[], steps: number): WsLeg[] {
  const live = legs.filter(isNewLeg);
  if (live.length < 2) return legs;
  const ks = live.map(l => l.strike);
  const mid = (Math.min(...ks) + Math.max(...ks)) / 2;
  return legs.map(l => {
    if (fixedLeg(l) || l.strike === mid) return l;
    const dir = l.strike > mid ? 1 : -1;
    const next = stepStrike(strikesFor(l.expiry), l.strike, dir * steps);
    // never let a narrow cross the centre
    if (steps < 0 && ((dir > 0 && next <= mid) || (dir < 0 && next >= mid))) return l;
    return { ...l, strike: next };
  });
}

/** Hedge distance: move every BUY leg further from (+) / closer to (−) its nearest SELL leg. */
export function hedgeLegs(legs: WsLeg[], strikesFor: (iso: string) => number[], steps: number): WsLeg[] {
  const sells = legs.filter(l => isNewLeg(l) && l.action === 'SELL');
  if (!sells.length) return legs;
  return legs.map(l => {
    if (fixedLeg(l) || l.action !== 'BUY') return l;
    const same = sells.filter(s => s.optionType === l.optionType);
    const pool = same.length ? same : sells;
    const ref = pool.reduce((p, c) => (Math.abs(c.strike - l.strike) < Math.abs(p.strike - l.strike) ? c : p), pool[0]);
    const dir = l.strike >= ref.strike ? 1 : -1;
    const next = stepStrike(strikesFor(l.expiry), l.strike, dir * steps);
    if (steps < 0 && ((dir > 0 && next <= ref.strike) || (dir < 0 && next >= ref.strike))) return l;
    return { ...l, strike: next };
  });
}

/** Strike-control readouts (USD): width = outer span, hedge = mean BUY→nearest SELL distance. */
export function structureReadout(legs: WsLeg[]) {
  const live = legs.filter(l => isNewLeg(l) && l.enabled);
  const ks = live.map(l => l.strike);
  const width = ks.length >= 2 ? Math.max(...ks) - Math.min(...ks) : null;
  const sells = live.filter(l => l.action === 'SELL');
  const buys = live.filter(l => l.action === 'BUY');
  let hedge: number | null = null;
  if (sells.length && buys.length) {
    const d = buys.map(b => Math.min(...sells.map(s => Math.abs(s.strike - b.strike))));
    hedge = d.reduce((a, b) => a + b, 0) / d.length;
  }
  return { width, hedge };
}

// ── naming ───────────────────────────────────────────────────────────────────

/** Recognise the common structures so a hand-built basket gets a real name + template id. */
export function classifyLegs(legs: WsLeg[]): { id: string; name: string } {
  const L = legs.filter(l => l.enabled && isNewLeg(l));
  const sig = (l: WsLeg) => `${l.action[0]}${l.optionType}`;
  const sorted = [...L].sort((a, b) => a.strike - b.strike);
  const sameExp = new Set(L.map(l => l.expiry)).size <= 1;
  const eqLots = new Set(L.map(l => l.lots)).size <= 1;
  if (!L.length) return { id: 'custom', name: 'Custom' };
  if (!sameExp) return { id: 'custom', name: 'Calendar / custom' };
  const s = sorted.map(sig).join(',');
  if (L.length === 1) {
    const m: Record<string, [string, string]> = { BCE: ['long_call', 'Long Call'], BPE: ['long_put', 'Long Put'], SCE: ['custom', 'Short Call'], SPE: ['custom', 'Short Put'] };
    const r = m[sig(L[0])]; return { id: r[0], name: r[1] };
  }
  if (L.length === 2 && eqLots) {
    const [a, b] = sorted;
    const same = a.strike === b.strike;
    if (s === 'BCE,SCE') return { id: 'bull_call_spread', name: 'Bull Call Spread' };
    if (s === 'SCE,BCE') return { id: 'bear_call_spread', name: 'Bear Call Spread' };
    if (s === 'BPE,SPE') return { id: 'bull_put_spread', name: 'Bull Put Spread' };
    if (s === 'SPE,BPE') return { id: 'bear_put_spread', name: 'Bear Put Spread' };
    const set = new Set(L.map(sig));
    if (set.has('BCE') && set.has('BPE')) return same ? { id: 'long_straddle', name: 'Long Straddle' } : { id: 'long_strangle', name: 'Long Strangle' };
    if (set.has('SCE') && set.has('SPE')) return same ? { id: 'short_straddle', name: 'Short Straddle' } : { id: 'short_strangle', name: 'Short Strangle' };
  }
  if (L.length === 2 && !eqLots) {
    const [a, b] = sorted;
    if (a.optionType === b.optionType && a.action !== b.action) {
      const sell = L.find(l => l.action === 'SELL')!, buy = L.find(l => l.action === 'BUY')!;
      if (buy.lots > sell.lots) return { id: 'custom', name: `${a.optionType === 'CE' ? 'Call' : 'Put'} Ratio Back-Spread (${sell.lots}×${buy.lots})` };
      return { id: 'custom', name: `${a.optionType === 'CE' ? 'Call' : 'Put'} Ratio Spread (${buy.lots}×${sell.lots})` };
    }
  }
  if (L.length === 4 && eqLots) {
    if (s === 'BPE,SPE,SCE,BCE') return { id: 'iron_condor', name: 'Iron Condor' };
    if ((s === 'BPE,SPE,SCE,BCE' || s === 'BPE,SCE,SPE,BCE' || s === 'BPE,SPE,SCE,BCE') && sorted[1].strike === sorted[2].strike) return { id: 'iron_butterfly', name: 'Iron Butterfly' };
    const mid = sorted[1].strike === sorted[2].strike && sorted[1].action === 'SELL' && sorted[2].action === 'SELL' && sorted[0].action === 'BUY' && sorted[3].action === 'BUY';
    if (mid && sorted[0].optionType === 'PE' && sorted[3].optionType === 'CE') return { id: 'iron_butterfly', name: 'Iron Butterfly' };
  }
  return { id: 'custom', name: 'Custom' };
}

/** Greatest common divisor of leg lots — placement sends legs repeated by lots/gcd × gcd lots. */
export function lotsGcd(lots: number[]): number {
  const g = (a: number, b: number): number => (b ? g(b, a % b) : a);
  return lots.reduce((a, b) => g(a, b), lots[0] || 1) || 1;
}

// ── monthly-strangle playbook helpers ───────────────────────────────────────

/** The short-strangle pair: exactly one short call and one short put among `legs`
 *  (same expiry). Returns their current prices and the premium ratio (dear ÷ cheap). */
export function shortPair(legs: WsLeg[]): { ce: WsLeg; pe: WsLeg; ratio: number; cheap: OptType; straddle: boolean } | null {
  const sells = legs.filter(l => l.enabled && !l.closing && l.action === 'SELL');
  const ces = sells.filter(l => l.optionType === 'CE'), pes = sells.filter(l => l.optionType === 'PE');
  if (ces.length !== 1 || pes.length !== 1) return null;
  const ce = ces[0], pe = pes[0];
  const pc = ce.mark != null && ce.mark > 0 ? ce.mark : ce.price, pp = pe.mark != null && pe.mark > 0 ? pe.mark : pe.price;
  if (ce.expiry !== pe.expiry || !(pc > 0) || !(pp > 0)) return null;
  const ratio = Math.max(pc, pp) / Math.min(pc, pp);
  return { ce, pe, ratio, cheap: pc < pp ? 'CE' : 'PE', straddle: ce.strike <= pe.strike };
}

/**
 * Strike on side `t` whose live price is closest to `target`, never crossing `limit`
 * (the other short's strike: a call may not go below it, a put not above it — that
 * crossing point is the straddle where the playbook stops adjusting).
 * `quotes` maps strike → price for that side and expiry.
 */
export function matchStrike(t: OptType, quotes: Array<{ strike: number; price: number }>, target: number, limit: number | null): { strike: number; price: number; clamped: boolean } | null {
  const ok = quotes.filter(q => q.price > 0 && (limit == null || (t === 'CE' ? q.strike >= limit : q.strike <= limit)));
  if (!ok.length) return null;
  const best = ok.reduce((p, c) => (Math.abs(c.price - target) < Math.abs(p.price - target) ? c : p), ok[0]);
  const clamped = limit != null && best.strike === limit && best.price < target * 0.9;
  return { ...best, clamped };
}

/** Delta India options taker fee for one side (USD): min(rate × notional, 3.5% × premium) + 18% GST.
 *  Mirrors the backend's perSideFeeUsd (optionStrategy/config.ts); `ratePct` is % of notional. */
export function feeUsd(premium: number, spot: number, lots: number, cv: number, ratePct = 0.03): number {
  const notional = Math.abs(spot) * cv * lots;
  const prem = Math.abs(premium) * cv * lots;
  return Math.min((ratePct / 100) * notional, 0.035 * prem) * 1.18;
}

/** Delta monthly expiry = the last Friday of its month. */
export function isMonthlyExpiry(iso: string): boolean {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (d.getUTCDay() !== 5) return false;
  const next = new Date(d.getTime() + 7 * 86400e3);
  return next.getUTCMonth() !== d.getUTCMonth();
}

/**
 * Historical move odds: over daily closes, the share of N-day windows in which BTC ended
 * (terminal) or traded (touch, on highs/lows) beyond the given % distances. `loPct` is
 * negative (e.g. -0.13), `hiPct` positive. Windows step one day.
 */
export function moveOdds(bars: Array<{ high: number; low: number; close: number }>, N: number, loPct: number | null, hiPct: number | null) {
  if (N < 1 || bars.length < N + 30) return null;
  let n = 0, endOut = 0, touchOut = 0;
  for (let i = 0; i + N < bars.length; i++) {
    const c0 = bars[i].close; if (!(c0 > 0)) continue;
    let hi = -Infinity, lo = Infinity;
    for (let j = i + 1; j <= i + N; j++) { if (bars[j].high > hi) hi = bars[j].high; if (bars[j].low < lo) lo = bars[j].low; }
    const end = bars[i + N].close / c0 - 1;
    const up = hi / c0 - 1, dn = lo / c0 - 1;
    n++;
    if ((hiPct != null && end >= hiPct) || (loPct != null && end <= loPct)) endOut++;
    if ((hiPct != null && up >= hiPct) || (loPct != null && dn <= loPct)) touchOut++;
  }
  return n ? { windows: n, endOut: endOut / n, touchOut: touchOut / n } : null;
}
